import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import NotificationCenter from '../Platform/NotificationCenter';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentUser } from '../slices/userManagementSlice';
import { useTenant } from '../config/tenantConfig';
import { useDarkMode } from '../DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useChatContext } from '../context/ChatContext';
import { useSidePanel } from '../context/SidePanelContext';
import { useWhiteLabel } from '../context/WhiteLabelContext';
import FloatingPlatformChat from '../Platform/FloatingPlatformChat';
import SystemStatusIndicator from '../components/SystemStatusIndicator';
import SupportChatAgent from '../components/SupportChatAgent';
import LandingGuideAgent from '../components/LandingGuideAgent';
import GlassTopBar from '../components/GlassTopBar';
import Swal from 'sweetalert2';
import api from '../lib/api';
import { usePermissions as useRolePermissions } from '../auth/usePermissions';
import { usePermissions as useHookPermissions } from '../hooks/usePermissions';
import { getEvaAppUrl } from '../config/landingLinks';
import { SIDEBAR_ITEM_KEYS } from '../auth/permissionsConfig';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import PageErrorBoundary from '../components/ErrorBoundary/PageErrorBoundary';
import TrialBanner from '../components/subscription/TrialBanner';
import { getHostname, hostMatchesList, toDomainList } from '../config/domainUtils';
import {
  LayoutDashboard,
  ClipboardCheck,
  Users,
  User,
  Building2,
  FileText,
  BarChart3,
  Database,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  Wrench,
  Sun,
  Moon,
  TrendingUp,
  LineChart,
  PieChart,
  FileBarChart,
  Loader2,
  UserPlus,
  MessageCircle,
  FileSignature,
  Banknote,
  Wallet,
  Shield,
  Coins,
  Bot,
  Target,
  FileCheck,
  Home,
  Newspaper,
  LayoutGrid,
  Umbrella
} from 'lucide-react';

const SECTION_ICON_MAP = {
  company: Building2,
  trade_partner: Users,
  invoice_analysis: FileText,
  validation_queue: ClipboardCheck,
  document: FileText,
  user: User
};

/**
 * Layout pour l'application Platform
 * UX complète pour les utilisateurs de la plateforme
 */
const PlatformLayout = ({ children }) => {
  const { showSidePanel: isSidebarOpen, setShowSidePanel: setIsSidebarOpen, isMobile } = useSidePanel();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [activeSelection, setActiveSelection] = useState(null);
  const [unreadChatMessages, setUnreadChatMessages] = useState(0);
  const [trialInfo, setTrialInfo] = useState(null);
  const prevUnreadRef = useRef(0); // Track previous unread count for notification comparison
  const searchRequestIdRef = useRef(0);
  const searchDebounceRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchListRef = useRef(null);
  const SEARCH_MIN_LENGTH = 2;
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantName, theme: staticTheme } = useTenant();
  const currentUser = useSelector(state => state.userManagement.currentUser);
  const validationStats = useSelector(state => state.validation?.stats);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user: authUser, isAuthenticated, logout } = useAuth();
  const { totalUnread } = useChatContext();
  const { branding, availableProfiles, currentProfileCode, switchProfile, fetchAvailableProfiles } = useWhiteLabel();

  // White-label context values available: branding?.primaryColor, branding?.companyName, etc.

  // Use white-label branding if available, otherwise fall back to static tenant config
  // IMPORTANT: brandName is ALWAYS "HaliTrade" or staticTheme.brandName - never the white-label company name
  const theme = {
    ...staticTheme,
    primary: branding?.primaryColor || staticTheme.primary,
    secondary: branding?.secondaryColor || staticTheme.secondary,
    accent: branding?.accentColor || staticTheme.accent,
    background: branding?.backgroundColor || '#111827',
    textColor: branding?.textColor || '#FFFFFF',
    logo: staticTheme.logo, // Always use HaliTrade/HaliAgent logo, not white-label logo
    brandName: staticTheme.brandName || 'HaliTrade', // ALWAYS HaliTrade/HaliAgent, never white-label company name
    tagline: branding?.tagline || staticTheme.tagline,
    // Store white-label company name separately for display in profile selector
    whiteLabelCompanyName: branding?.companyName || null
  };
  const platformLogo = theme.logo || `${process.env.PUBLIC_URL || ''}/assets/halisoft-monogram.svg`;
  const { hasAccessTo } = useRolePermissions();
  const { can } = useHookPermissions();
  const displayUser = currentUser || authUser || null;
  const displayName = displayUser
    ? `${displayUser.firstName || displayUser.first_name || ''} ${displayUser.lastName || displayUser.last_name || ''}`.trim()
    : '';
  const displayRole = displayUser?.role || null;
  const displayCategory = displayUser?.category || null;
  const userEmail = displayUser?.email || '';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // In autoCollapse mode (desktop), main content always uses the collapsed width (80px)
  // The expanded sidebar overlays on top of content
  // On mobile, sidebar overlays everything — no margin shift
  const sidebarWidth = isMobile ? 0 : (isSidebarOpen ? 80 : 0);
  const dispatch = useDispatch();

  // Check if user is a platform admin (can access support chat admin features)
  const isPlatformAdmin = useMemo(() => {
    if (!authUser) return false;
    const userCategory = authUser.category || authUser.role || '';
    return userCategory === 'platform' ||
      userCategory === 'platform_user' ||
      userCategory === 'platform_admin' ||
      authUser.role === 'admin';
  }, [authUser]);

  // Sync AuthContext user to Redux for permission hooks
  useEffect(() => {
    if (authUser) {
      // Only update if Redux state is empty or different
      if (!currentUser || currentUser.email !== authUser.email) {
        // Debug only: console.log('[PlatformLayout] Syncing AuthContext user to Redux:', authUser.email);
        dispatch(setCurrentUser(authUser));
      }
    }
  }, [authUser, currentUser, dispatch]);

  // Fetch available profiles when user is authenticated
  useEffect(() => {
    if (isAuthenticated && availableProfiles.length === 0 && fetchAvailableProfiles) {
      // Debug only: console.log('[PlatformLayout] User authenticated, fetching white-label profiles...');
      fetchAvailableProfiles();
    }
  }, [isAuthenticated, availableProfiles.length, fetchAvailableProfiles]);

  // Fetch trial info — check all modules and show the one expiring soonest
  useEffect(() => {
    if (!isAuthenticated) return;
    const modules = ['risk-analysis', 'hali-leads', 'contract-analyzer', 'invoice-financing-check', 'past-trade-studio', 'haliform', 'global'];
    Promise.all(
      modules.map(m => api.get(`/api/subscriptions/current?module=${m}`).then(r => r.data).catch(() => null))
    ).then(results => {
      const trial = results
        .filter(r => r?.is_trial && r?.trial_days_remaining != null)
        .sort((a, b) => a.trial_days_remaining - b.trial_days_remaining)[0];
      if (trial) setTrialInfo({ daysRemaining: trial.trial_days_remaining, module: trial.module });
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isSidebarOpen) {
      setSidebarCollapsed(false);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const trimmed = searchTerm.trim();
    if (!trimmed || trimmed.length < SEARCH_MIN_LENGTH) {
      setSearchLoading(false);
      setSearchError('');
      setSearchResults([]);
      setActiveSelection(null);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      const requestId = Date.now();
      searchRequestIdRef.current = requestId;
      setSearchLoading(true);
      try {
        const { data } = await api.get('/api/search/global', { params: { q: trimmed } });
        if (searchRequestIdRef.current !== requestId) return;
        const sections = Array.isArray(data?.sections) ? data.sections : [];
        setSearchResults(sections);
        setSearchError('');
        const defaultSection = sections.find(
          (section) => Array.isArray(section.results) && section.results.length
        );
        if (defaultSection) {
          setActiveSelection({
            sectionType: defaultSection.type,
            entityId: String(defaultSection.results[0].entityId)
          });
        } else {
          setActiveSelection(null);
        }
      } catch (error) {
        if (searchRequestIdRef.current !== requestId) return;
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to perform search.';
        setSearchError(message);
        setSearchResults([]);
        setActiveSelection(null);
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, 350);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showSearchResults) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearchResults]);

  useEffect(() => {
    const hasAnyResult = searchResults.some(
      (section) => Array.isArray(section.results) && section.results.length
    );
    if (!hasAnyResult) {
      if (activeSelection !== null) {
        setActiveSelection(null);
      }
      return;
    }
    const selectionExists = activeSelection
      ? searchResults.some(
        (section) =>
          section.type === activeSelection.sectionType &&
          (section.results || []).some(
            (result) => String(result.entityId) === String(activeSelection.entityId)
          )
      )
      : false;

    if (!selectionExists) {
      const fallback = searchResults.find(
        (section) => Array.isArray(section.results) && section.results.length
      );
      if (fallback) {
        setActiveSelection({
          sectionType: fallback.type,
          entityId: String(fallback.results[0].entityId)
        });
      }
    }
  }, [searchResults, activeSelection]);

  const totalResults = useMemo(
    () =>
      searchResults.reduce(
        (sum, section) => sum + (Array.isArray(section.results) ? section.results.length : 0),
        0
      ),
    [searchResults]
  );

  const trimmedSearch = searchTerm.trim();
  const hasResults = totalResults > 0;

  const activeSection = useMemo(() => {
    if (!activeSelection) return null;
    return (
      searchResults.find((section) => section.type === activeSelection.sectionType) || null
    );
  }, [searchResults, activeSelection]);

  const activeItem = useMemo(() => {
    if (!activeSection || !activeSelection) return null;
    return (
      (activeSection.results || []).find(
        (result) => String(result.entityId) === String(activeSelection.entityId)
      ) || null
    );
  }, [activeSection, activeSelection]);

  const searchResultsVisible =
    showSearchResults &&
    (trimmedSearch.length > 0 || searchLoading || searchError || hasResults);

  const getBadgeClasses = (variant) => {
    const base = 'px-2 py-0.5 rounded-full text-xs font-semibold border';
    const tone = (variant || 'neutral').toLowerCase();
    if (darkMode) {
      if (tone === 'success') return `${base} bg-green-900/40 text-green-200 border-green-700`;
      if (tone === 'warning') return `${base} bg-yellow-900/30 text-yellow-200 border-yellow-700`;
      if (tone === 'danger') return `${base} bg-red-900/40 text-red-200 border-red-700`;
      if (tone === 'info') return `${base} bg-blue-900/40 text-blue-200 border-blue-700`;
      return `${base} bg-gray-800 text-gray-300 border-gray-700`;
    }
    if (tone === 'success') return `${base} bg-green-100 text-green-700 border-green-200`;
    if (tone === 'warning') return `${base} bg-yellow-100 text-yellow-700 border-yellow-200`;
    if (tone === 'danger') return `${base} bg-red-100 text-red-700 border-red-200`;
    if (tone === 'info') return `${base} bg-blue-100 text-blue-700 border-blue-200`;
    return `${base} bg-gray-100 text-gray-600 border-gray-200`;
  };

  const formatMetadataValue = (meta) => {
    if (!meta) return '—';
    const { value, type, currency } = meta;
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return '—';
    }
    if (type === 'datetime' || type === 'date') {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return type === 'date' ? date.toLocaleDateString() : date.toLocaleString();
      }
      return String(value);
    }
    if (type === 'currency') {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 2
          }).format(numeric);
        } catch (_) {
          return `${numeric.toLocaleString()} ${currency || ''}`.trim();
        }
      }
      return `${value} ${currency || ''}`.trim();
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  const handleActivateResult = (section, result) => {
    if (!section || !result) return;
    setActiveSelection({
      sectionType: section.type,
      entityId: String(result.entityId)
    });
  };

  const handleSelectSearchResult = (result, sectionTypeOverride) => {
    if (!result) return;
    searchRequestIdRef.current += 1;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const sectionType =
      sectionTypeOverride || activeSelection?.sectionType || result.entityType;
    try {
      window.dispatchEvent(
        new CustomEvent('global-search:open-record', {
          detail: { ...result, sectionType }
        })
      );
    } catch (_) { }
    setShowSearchResults(false);
    setSearchTerm('');
    setSearchResults([]);
    setActiveSelection(null);
    setSearchError('');
    setSearchLoading(false);
    if (result.path) {
      if (result.navigateState) {
        navigate(result.path, { state: result.navigateState });
      } else {
        navigate(result.path);
      }
    }
  };

  const handleSearchInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const firstSection = searchResults.find(
        (section) => Array.isArray(section.results) && section.results.length
      );
      if (firstSection) {
        const firstResult = firstSection.results[0];
        setActiveSelection({
          sectionType: firstSection.type,
          entityId: String(firstResult.entityId)
        });
        handleSelectSearchResult(firstResult, firstSection.type);
      }
    } else if (event.key === 'ArrowDown') {
      if (!searchResultsVisible) return;
      event.preventDefault();
      const firstButton = searchListRef.current?.querySelector('button[data-result-index="0"]');
      if (firstButton) firstButton.focus();
    }
  };

  const platformBase = '/platform';

  // ═══ Sidebar: grouped sections with permission filtering ═══
  const iconColorMap = {
    'HaliDashboard': '#DC2626', 'EVA': '#DC2626', 'HaliSuite': '#7c3aed', 'Legal Agent': '#6d28d9',
    'Financing Hub': '#7c3aed', 'Invoice Market': '#4f46e5', 'Fund Wallets': '#059669',
    'ARPA Generator': '#ea580c', 'Wallet Approvals': '#0d9488', 'Deal Approvals': '#0284c7',
    'My Wallet': '#4f46e5', 'Withdraw Funds': '#2563eb', 'Risk Analysis': '#dc2626',
    'HaliRisk v2': '#be123c', 'LC DocCheck': '#059669', 'Validation Queue': '#dc2626',
    'Credit Insurance': '#0e7490',
    'Sent Offers': '#0891b2', 'Doc Review': '#db2777', 'Client Companies': '#059669',
    'Trade Partners': '#0f766e', 'Customer Chat': '#db2777', 'Agent Referrals': '#7c3aed',
    'Lead Pipeline': '#d97706', 'Finance Team': '#d97706',
    'Analytics Dashboard': '#db2777', 'Offer Tracking': '#0891b2', 'Credit Limits': '#0891b2',
    'Financed Deals': '#dc2626', 'AUM Overview': '#6d28d9', 'Sales Pipeline': '#059669',
    'HaliBlog': '#3b82f6', 'HaliPass': '#8b5cf6',
    'Data Explorer': '#7c3aed', 'User Management': '#2563eb', 'Platform Settings': '#ea580c',
    'HaliForm': '#6366f1', 'Components': '#8b5cf6',
  };

  const mi = (icon, label, path, permission = null, extra = {}) => {
    const hasItemKeyAccess = !extra.itemKey || hasAccessTo(extra.itemKey);
    const hasPermission = !permission || can(permission);
    if (!hasItemKeyAccess || !hasPermission) return null;
    return { icon, label, path, color: iconColorMap[label] || theme.primary, ...extra };
  };
  const fc = (items) => items.filter(Boolean);

  const sidebarMenuItems = [
    // ─── Dashboard (top-level, always visible) ───
    mi(Home, 'HaliDashboard', `${platformBase}/dashboard`),

    // ─── OPERATIONS ───
    { type: 'section', label: 'Operations', color: '#7c3aed', children: fc([
      mi(MessageCircle, 'EVA', getEvaAppUrl(), null, { external: true }),
      mi(Wrench, 'HaliSuite', `${platformBase}/factor-tools`),
      mi(Bot, 'Legal Agent', `${platformBase}/factor-tools/legal-agent`),
      mi(FileText, 'ARPA Generator', `${platformBase}/arpa`, 'canViewFinancing'),
      mi(ClipboardCheck, 'Validation Queue', `${platformBase}/validation-queue`, 'canValidateData', { badge: validationStats?.pending || 0 }),
      mi(Shield, 'HaliPass', `${platformBase}/halipass`),
      mi(FileSignature, 'Sent Offers', `${platformBase}/sent-termsheets`),
    ]) },

    // ─── FINANCING ───
    { type: 'section', label: 'Financing', color: '#7c3aed', children: fc([
      mi(Banknote, 'Financing Hub', `${platformBase}/financing-hub`, 'canViewFinancing'),
      mi(LayoutDashboard, 'Invoice Market', `${platformBase}/financing-hub/marketplace`, 'canViewFinancing', { itemKey: SIDEBAR_ITEM_KEYS.MARKETPLACE }),
      mi(Wallet, 'Fund Wallets', `${platformBase}/financing-hub/wallets`, 'canViewFinancing'),
      mi(ClipboardCheck, 'Wallet Approvals', `${platformBase}/wallet-approvals`, 'canViewFinancing'),
      mi(ClipboardCheck, 'Deal Approvals', `${platformBase}/approvals`),
      mi(Wallet, 'My Wallet', `${platformBase}/wallet`),
      mi(Banknote, 'Withdraw Funds', `${platformBase}/financing-hub/withdraw`, 'canViewFinancing'),
    ]) },

    // ─── COMPONENTS (réutilisables: HaliForm, HaliDoc, LC DocCheck) ───
    { type: 'section', label: 'Components', color: '#8b5cf6', children: fc([
      mi(LayoutGrid, 'HaliForm', `${platformBase}/haliform`, 'canViewCompanies'),
      mi(FileText, 'Doc Review', `${platformBase}/halidoc`, 'canViewCompanies'),
      mi(FileCheck, 'LC DocCheck', `${platformBase}/lctradedoccheck`),
    ]) },

    // ─── RISK & COMPLIANCE ───
    { type: 'section', label: 'Risk & Compliance', color: '#dc2626', children: fc([
      mi(Shield, 'Risk Analysis', `${platformBase}/financing-hub/risk-analysis`, 'canViewFinancing'),
      mi(Shield, 'HaliRisk v2', `${platformBase}/halitrade/halirisk-playbook`),
      mi(Shield, 'Platform Risk Matrix', `${platformBase}/risk-matrix`),
      mi(Umbrella, 'Credit Insurance', `${platformBase}/credit-insurance`),
    ]) },

    // ─── CRM ───
    { type: 'section', label: 'CRM', color: '#059669', children: fc([
      mi(Building2, 'Client Companies', `${platformBase}/companies`),
      mi(Users, 'Trade Partners', `${platformBase}/trade-partners`),
      mi(MessageCircle, 'Customer Chat', `${platformBase}/customer-chat`, null, { badge: totalUnread || 0 }),
      mi(UserPlus, 'Agent Referrals', `${platformBase}/referrals`),
      mi(Target, 'Lead Pipeline', '/platform/leads'),
    ]) },

    // ─── ANALYTICS ───
    { type: 'section', label: 'Analytics', color: '#db2777', children: fc([
      mi(BarChart3, 'Analytics Dashboard', `${platformBase}/analytics`, 'canViewAnalytics'),
      mi(TrendingUp, 'Offer Tracking', `${platformBase}/analytics/termsheets`, 'canViewAnalytics'),
      mi(LineChart, 'Credit Limits', `${platformBase}/analytics/credit-limit`, 'canViewAnalytics'),
      mi(FileBarChart, 'Financed Deals', `${platformBase}/analytics/financed`, 'canViewAnalytics'),
      mi(PieChart, 'AUM Overview', `${platformBase}/analytics/aum`, 'canViewAnalytics'),
      mi(TrendingUp, 'Sales Pipeline', `${platformBase}/analytics/opportunities`, 'canViewAnalytics'),
    ]) },

    // ─── ADMIN ───
    { type: 'section', label: 'Admin', color: '#2563eb', children: fc([
      mi(Newspaper, 'HaliBlog', `${platformBase}/haliblog`),
      mi(Banknote, 'Product Pricing', `${platformBase}/product-pricing`),
      mi(Banknote, 'Repayment Pricing', `${platformBase}/repayment-pricing`),
      mi(Coins, 'Finance Team', `${platformBase}/financing-hub/finance-team`, 'canViewFinancing', { itemKey: SIDEBAR_ITEM_KEYS.FINANCE_TEAM }),
      mi(Database, 'Data Explorer', `${platformBase}/data-explorer`, 'canViewDataExplorer'),
      mi(Users, 'User Management', `${platformBase}/user-management`, 'canViewUsers'),
      mi(Settings, 'Platform Settings', `${platformBase}/settings/permissions`),
    ]) },
  ].filter((entry) => {
    if (!entry) return false;
    if (entry.type === 'section') return entry.children && entry.children.length > 0;
    return true;
  });

  // (grouped sections defined above)
  const handleSidebarNavigate = (path) => {
    navigate(path);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBrandClick = () => {
    navigate('/platform/dashboard');
  };

  const [isChatOpen, setIsChatOpen] = useState(false);
  const handleNotificationsClick = () => {
    // Navigate to Customer Chat (Contact Center) for platform users
    navigate('/platform/customer-chat');
  };

  let resultItemCounter = -1;

  // Tenant guard: Block HaliTrade users (separate platform)
  // Allow HaliFlow and Platform users (same system)
  // Also allow users from trusted app domains (env-configured)
  useEffect(() => {
    // console.log('[PlatformLayout] Tenant guard check');

    if (isAuthenticated && authUser) {
      // Check if user is coming from a trusted domain - always allow
      const hostname = getHostname();
      const trustedDomains = toDomainList(
        process.env.REACT_APP_PLATFORM_ALLOWED_DOMAINS ||
        process.env.REACT_APP_TRUSTED_DOMAINS ||
        process.env.REACT_APP_APP_DOMAINS ||
        ''
      );
      const isTrustedDomain = trustedDomains.length > 0 && hostMatchesList(hostname, trustedDomains);

      if (isTrustedDomain) {
        // console.log('[PlatformLayout] ✅ User from trusted domain - access granted');
        return;
      }

      const userCategory = authUser.category || authUser.role || '';

      // Platform users can access /halitrade/* routes - don't check tenant for them
      const isPlatformUser = userCategory === 'platform' ||
        userCategory === 'platform_user' ||
        userCategory === 'platform_admin' ||
        authUser.role === 'admin';

      // Only block actual halitrade/agent users by category, NOT by route tenant
      const isHaliTradeUser = !isPlatformUser && (
        userCategory === 'halitrade' ||
        userCategory === 'halitrade_user' ||
        userCategory === 'agent'
      );

      if (isHaliTradeUser) {
        console.warn('[PlatformLayout] ❌ HaliTrade user blocked. Redirecting to login.');

        // Logout and redirect to Platform login
        logout();
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 150);
      } else {
        // console.log('[PlatformLayout] ✅ Platform user - access granted.');
      }
    }
  }, [isAuthenticated, authUser, logout, navigate]);

  // Poll for unread customer chat messages (messages from customers to platform)
  // Only for platform admins who can access support chat
  useEffect(() => {
    if (!isPlatformAdmin) return;

    let cancelled = false;
    let timer = null;
    let delayMs = 8000;
    const MIN_DELAY = 8000;
    const MAX_DELAY = 60000;

    const schedule = () => {
      if (cancelled) return;
      timer = setTimeout(pollUnreadCustomerMessages, delayMs);
    };

    const pollUnreadCustomerMessages = async () => {
      try {
        const lastReadKey = 'platformLastReadMessages';
        const lastReadMap = JSON.parse(localStorage.getItem(lastReadKey) || '{}');

        const response = await api.get('/api/support/chat/unread-counts', {
          params: { lastRead: JSON.stringify(lastReadMap) },
          timeout: 15000,
        });

        if (response.data.success) {
          const newTotal = response.data.totalUnread || 0;

          if (newTotal > prevUnreadRef.current) {
            const newCount = newTotal - prevUnreadRef.current;
            try {
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleTs/k9zVnGM4RYvV0al2SUiG0M2LVD9JhtbUrIVbUYXKw6R8XFaJzsWkfF5YisnBn3laV4vNxKR8YFqKyb+YcFNWjdLIqoFhV4jOxaV6VlCG0sutglpKh9XUsohlVIjLvpd0VFqN08mriWxcho/H');
              audio.volume = 0.3;
              audio.play().catch(() => {});
            } catch (_) {}

            if ('Notification' in window && Notification.permission === 'granted') {
              try {
                new Notification('New Customer Message', {
                  body: `You have ${newCount} new message${newCount > 1 ? 's' : ''} from customers`,
                  icon: '/favicon.ico',
                  tag: 'customer-chat-unread'
                });
              } catch (_) {}
            } else if ('Notification' in window && Notification.permission === 'default') {
              Notification.requestPermission();
            }
          }

          prevUnreadRef.current = newTotal;
          setUnreadChatMessages(newTotal);
        }
        delayMs = MIN_DELAY;
      } catch (error) {
        // Render/CF 502 during deploys have no ACAO → browser "CORS" / Network Error.
        const msg = String(error?.message || '');
        delayMs = Math.min(Math.round(delayMs * 1.75), MAX_DELAY);
        if (!/network error|cors|err_failed|timeout|502|503|504/i.test(msg)) {
          console.log('[PlatformLayout] Error polling unread customer messages:', msg);
        }
      } finally {
        schedule();
      }
    };

    pollUnreadCustomerMessages();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isPlatformAdmin]);

  const handleLogout = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Confirm exit',
      text: 'Are you sure you want to leave the platform workspace?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, exit',
      cancelButtonText: 'Stay',
      reverseButtons: true,
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827',
      confirmButtonColor: theme.primary
    });

    if (isConfirmed) {
      logout();
      navigate('/haliflow-landing');
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <GlassTopBar
        logoSrc={theme.logo || `${process.env.PUBLIC_URL || ''}/logo.svg`}
        brandName={theme.brandName || 'HaliTrade'}
        brandSubtitle="powered by HaliSoft"
        displayName={displayName}
        displayRole={displayRole || 'User'}
        userCategory={displayCategory}
        userEmail={userEmail}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        isSidebarCollapsed={!isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onBrandClick={handleBrandClick}
        notifications={(validationStats?.pending || 0) + unreadChatMessages}
        onNotificationsClick={handleNotificationsClick}
        onLogout={handleLogout}
        borderColor={currentProfileCode && currentProfileCode !== 'none' && branding?.primaryColor ? (branding.primaryColor + '20') : null}
        primaryColor={currentProfileCode && currentProfileCode !== 'none' && branding?.primaryColor ? branding.primaryColor : null}
        accentColor={theme.primary}
        currentProfile={availableProfiles.length > 0 ? (currentProfileCode && currentProfileCode !== 'none' ? branding : { tenant_code: 'none' }) : null}
        availableProfiles={availableProfiles}
        onProfileChange={availableProfiles.length > 0 ? switchProfile : null}
      />

      {/* Search Bar - Moved outside header */}
      <div className="sticky top-14 z-20 px-4 sm:px-6 lg:px-8 pt-3 pointer-events-none">
        <div className="hidden md:flex flex-1 max-w-md mx-auto pointer-events-auto" ref={searchContainerRef}>
          <div className="relative w-full">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search companies, partners, documents..."
              value={searchTerm}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (!showSearchResults) {
                  setShowSearchResults(true);
                }
              }}
              onFocus={() => setShowSearchResults(true)}
              onKeyDown={handleSearchInputKeyDown}
              className={`
                    w-full pl-10 pr-4 py-2 rounded-lg border
                    ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}
                    focus:outline-none focus:ring-2
                  `}
              style={{ '--tw-ring-color': theme.primary }}
            />
            {searchResultsVisible && (
              <div
                className={`absolute left-0 right-0 top-full mt-2 z-50 rounded-xl shadow-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
              >
                {trimmedSearch.length < SEARCH_MIN_LENGTH && !searchLoading ? (
                  <div className={`p-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Type at least {SEARCH_MIN_LENGTH} characters to search.
                  </div>
                ) : searchError ? (
                  <div className="p-4 text-sm text-red-500">{searchError}</div>
                ) : !hasResults && !searchLoading ? (
                  <div className={`p-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    No matches for “{trimmedSearch}”.
                  </div>
                ) : (
                  <>
                    {searchLoading && (
                      <div
                        className={`px-4 py-2 text-xs flex items-center gap-2 ${darkMode ? 'text-blue-200' : 'text-blue-600'
                          }`}
                      >
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Searching…
                      </div>
                    )}
                    <div className="grid md:grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                      <div className="max-h-80 overflow-y-auto py-2" ref={searchListRef}>
                        {searchResults.map((section) => {
                          const Icon = SECTION_ICON_MAP[section.type] || Search;
                          const sectionResults = Array.isArray(section.results)
                            ? section.results
                            : [];
                          if (!sectionResults.length) return null;
                          return (
                            <div key={section.type} className="pb-1">
                              <div
                                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wide flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                  }`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                                {section.label || 'Results'}
                              </div>
                              <div className="space-y-1">
                                {sectionResults.map((result) => {
                                  resultItemCounter += 1;
                                  const isActive =
                                    activeSelection &&
                                    section.type === activeSelection.sectionType &&
                                    String(result.entityId) ===
                                    String(activeSelection.entityId);
                                  return (
                                    <button
                                      key={`${section.type}-${result.entityId}`}
                                      type="button"
                                      data-result-index={resultItemCounter}
                                      onMouseEnter={() => handleActivateResult(section, result)}
                                      onFocus={() => handleActivateResult(section, result)}
                                      onClick={() =>
                                        handleSelectSearchResult(result, section.type)
                                      }
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          handleSelectSearchResult(result, section.type);
                                        }
                                      }}
                                      className={`
                                            w-full text-left px-4 py-3 transition rounded-lg
                                            ${isActive
                                          ? darkMode
                                            ? 'bg-gray-700/70 border border-gray-600'
                                            : 'bg-gray-100 border border-gray-200'
                                          : darkMode
                                            ? 'hover:bg-gray-800'
                                            : 'hover:bg-gray-50'
                                        }
                                          `}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <div
                                            className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'
                                              }`}
                                          >
                                            {result.title}
                                          </div>
                                          {result.subtitle && (
                                            <div
                                              className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                                }`}
                                            >
                                              {result.subtitle}
                                            </div>
                                          )}
                                        </div>
                                        {result.badge && (
                                          <span className={getBadgeClasses(result.badgeVariant)}>
                                            {result.badge}
                                          </span>
                                        )}
                                      </div>
                                      {Array.isArray(result.metadata) &&
                                        result.metadata.length > 0 && (
                                          <div className="mt-2 grid gap-1 text-xs">
                                            {result.metadata.slice(0, 3).map((meta, idx) => (
                                              <div
                                                key={`${section.type}-${result.entityId}-meta-${idx}`}
                                                className={`${darkMode ? 'text-gray-300' : 'text-gray-600'
                                                  }`}
                                              >
                                                <span
                                                  className={`mr-1 uppercase ${darkMode
                                                    ? 'text-gray-400'
                                                    : 'text-gray-500'
                                                    }`}
                                                >
                                                  {meta.label}:
                                                </span>
                                                <span
                                                  className={`${darkMode
                                                    ? 'text-gray-200'
                                                    : 'text-gray-700'
                                                    }`}
                                                >
                                                  {formatMetadataValue(meta)}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div
                        className={`border-t lg:border-t-0 lg:border-l max-h-80 overflow-y-auto ${darkMode ? 'border-gray-700' : 'border-gray-200'
                          }`}
                      >
                        {activeItem ? (
                          <div className="p-4 space-y-3">
                            <div
                              className={`text-xs uppercase font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}
                            >
                              {activeSection?.label || 'Record Preview'}
                            </div>
                            <div
                              className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'
                                }`}
                            >
                              {activeItem.title}
                            </div>
                            {activeItem.subtitle && (
                              <div
                                className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'
                                  }`}
                              >
                                {activeItem.subtitle}
                              </div>
                            )}
                            {activeItem.badge && (
                              <span className={getBadgeClasses(activeItem.badgeVariant)}>
                                {activeItem.badge}
                              </span>
                            )}
                            {Array.isArray(activeItem.metadata) &&
                              activeItem.metadata.length > 0 ? (
                              <div className="space-y-2">
                                {activeItem.metadata.map((meta, idx) => (
                                  <div key={`${activeItem.entityId}-meta-${idx}`}>
                                    <div
                                      className={`text-xs uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'
                                        }`}
                                    >
                                      {meta.label}
                                    </div>
                                    <div
                                      className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'
                                        }`}
                                    >
                                      {formatMetadataValue(meta)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div
                                className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'
                                  }`}
                              >
                                No additional details.
                              </div>
                            )}
                            {activeItem.path && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleSelectSearchResult(
                                    activeItem,
                                    activeSelection?.sectionType
                                  )
                                }
                                className="inline-flex items-center px-3 py-2 text-sm font-semibold rounded-md text-white transition hover:opacity-90"
                                style={{ backgroundColor: theme.primary }}
                              >
                                Open Record
                              </button>
                            )}
                          </div>
                        ) : (
                          <div
                            className={`p-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'
                              }`}
                          >
                            Select a result to preview its details.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <CollapsibleSidebar
          className={!isSidebarOpen ? 'sidebar-hidden' : ''}
          style={{ top: '56px' }}
          logoSrc={currentProfileCode && branding?.logoUrl ? branding.logoUrl : (theme.logo || platformLogo)}
          brandName={theme.brandName || 'HaliTrade'}
          subtitle={branding?.tagline || ''}
          whiteLabelCompanyName={currentProfileCode && branding?.companyName ? branding.companyName : null}
          platformName="HaliTrade"
          viewBadge="Platform View"
          menuItems={sidebarMenuItems}
          activePath={location.pathname}
          onNavigate={handleSidebarNavigate}
          onLogoClick={() => navigate('/platform/dashboard')}
          onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
          primaryColor={currentProfileCode && branding?.primaryColor ? branding.primaryColor : null}
          accentColor={theme.primary}
          darkMode={darkMode}
          autoCollapse={true}
          onLogout={handleLogout}
        />

        {/* Main Content */}
        <main
          className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
          style={{ marginLeft: sidebarWidth }}
        >
          {trialInfo && (
            <TrialBanner daysRemaining={trialInfo.daysRemaining} module={trialInfo.module} />
          )}
          <div className="p-6 lg:p-8 pb-28">
            <div className="glass-surface glass-panel rounded-[28px] p-6 lg:p-8 shadow-[0_35px_120px_rgba(15,23,42,0.15)]">
              <PageErrorBoundary>
                {children || <Outlet />}
              </PageErrorBoundary>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {
        isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )
      }

      {/* Floating Chat Widget - Only for platform admins (support chat admin). Hidden on HaliForm to avoid overlap with guide agent. */}
      {isPlatformAdmin && !location.pathname.includes('/haliform') && <FloatingPlatformChat />}

      {/* HaliGuide - Hidden on platform dashboard to avoid overlapping card grid */}
      {!location.pathname.endsWith('/dashboard') && (
        <LandingGuideAgent darkMode={darkMode} theme="default" position="left" />
      )}

      {/* Support Chat Agent - Contact Center */}
      {
        isChatOpen && (
          <div className="fixed bottom-4 right-4 z-50">
            <SupportChatAgent initiallyOpen={true} onOpenChange={(open) => { if (!open) setIsChatOpen(false); }} />
          </div>
        )
      }

      {/* System Status Indicator */}
      <SystemStatusIndicator darkMode={darkMode} inline />
    </div >
  );
};

export default PlatformLayout;
